/**
 * Web chat tool-card for the research tools (`webSearch`, `readUrl`,
 * `readPdf`, `readGithubRepo`, `readSkill`), drawn as Gemini draws them: not a
 * card around the results but one collapsible line above the answer,
 * "Searched the web" or "Read <domain or name>", collapsed by default. Open,
 * a flat box holds the brief (paragraphs and bullets), the queries as chips,
 * each URL read with its outcome, the repository and commit, or the skill.
 * Sources stay in the Sources row under the answer.
 * `research-card.native.tsx` is the twin; both read `research-card-model.ts`.
 */
import React, { useId, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardBadge } from './card-ui';
import {
  BRIEF_PREVIEW_CHARS,
  RESEARCH_ACTIVITY_DEFAULT_OPEN,
  collapseBrief,
  parseResearchResult,
  researchActivityHasBody,
  researchActivityLabel,
  type ResearchCardKind,
} from './research-card-model';
import type { ToolCardProps } from './types';

const KIND_ICONS: Record<ResearchCardKind, string> = {
  search: 'travel_explore',
  url: 'link',
  pdf: 'picture_as_pdf',
  github: 'code',
  skill: 'menu_book',
};

export const ResearchCard: FC<ToolCardProps> = ({ toolName, result }) => {
  const { t } = useTranslation('common');
  const model = useMemo(() => parseResearchResult(toolName, result), [toolName, result]);
  const [open, setOpen] = useState(RESEARCH_ACTIVITY_DEFAULT_OPEN);
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();

  if (!model) return null;

  const label = researchActivityLabel(model);
  const text =
    label?.key === 'read'
      ? t('chat.research.activity.read', { name: label.name })
      : label?.key === 'search'
        ? t('chat.research.activity.search')
        : t(`chat.research.title.${model.kind}`);
  const hasBody = researchActivityHasBody(model);
  const brief = collapseBrief(model.blocks, expanded ? Number.POSITIVE_INFINITY : BRIEF_PREVIEW_CHARS);

  return (
    <div className="tabbio-research-activity">
      <button
        type="button"
        className="tabbio-research-line"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={hasBody ? bodyId : undefined}
        disabled={!hasBody}
      >
        <MaterialSymbol name={KIND_ICONS[model.kind]} size={16} />
        <span dir="auto" className="tabbio-research-line-label">
          {text}
        </span>
        {hasBody ? <MaterialSymbol name={open ? 'expand_less' : 'expand_more'} size={16} /> : null}
      </button>

      {open && hasBody ? (
        <div id={bodyId} className="tabbio-research-box">
          {model.repository ? (
            <div className="tabbio-research-row">
              <span dir="auto" className="tabbio-research-row-title">
                {model.repository.name}
              </span>
              <span className="tabbio-research-row-meta">
                {[
                  model.repository.sha ? t('chat.research.commit', { sha: model.repository.sha }) : null,
                  model.repository.treeTruncated
                    ? t('chat.research.treeTruncated')
                    : t('chat.research.filesRead', { files: model.repository.filesRead }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          ) : null}

          {model.skill ? (
            <div className="tabbio-research-row">
              <span dir="auto" className="tabbio-research-row-title">
                {model.skill.name}
              </span>
              {model.skill.version ? (
                <span className="tabbio-research-row-meta">
                  {t('chat.research.skillVersion', { version: model.skill.version })}
                </span>
              ) : null}
            </div>
          ) : null}

          {brief.blocks.map((block, index) =>
            block.type === 'paragraph' ? (
              <p key={index} dir="auto" className="tabbio-research-text">
                {block.text}
              </p>
            ) : (
              <ul key={index} dir="auto" className="tabbio-research-list">
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            ),
          )}
          {brief.truncated || expanded ? (
            <button
              type="button"
              className="tabbio-text-button tabbio-research-more"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? t('chat.research.showLess') : t('chat.research.showMore')}
            </button>
          ) : null}

          {model.queries.length ? (
            <div
              className="tabbio-research-queries"
              role="group"
              aria-label={t('chat.research.searchedFor', { queries: model.queries.join(', ') })}
            >
              {model.queries.map((query) => (
                <span key={query} dir="auto" className="tabbio-research-query">
                  {query}
                </span>
              ))}
            </div>
          ) : null}

          {model.urls.map((row, index) => (
            <div key={`${row.url}-${index}`} className="tabbio-research-row is-url">
              <span dir="ltr" className="tabbio-research-row-title" title={row.url}>
                {row.label}
              </span>
              <CardBadge>{t(`chat.research.urlStatus.${row.status}`)}</CardBadge>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
